import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  signal,
} from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ErrorsImpl, TradingGroupImpl, XferAction, XferType } from "shared/cubiq-model-multi-asset";
import { OrderTransferResultImpl } from "shared/cubiq-model-multi-asset/model/impl/OrderTransferResultImpl";

import {
  ActiveUser,
  ActiveUserProfilePromiseService,
  BenchmarkDataPrimaryLookupService,
  BFXNotificationSubType,
  BFXNotificationType,
  ModelDeleteEvent,
  NotificationService,
  OrderAcceptancePromiseService,
  OrderDataActionPromiseService,
  OrderDataModelUpdateSource,
  OrderDataPromiseLookupService,
  OrderXferPromiseService,
  Roles,
  SystemConfigPromiseService,
  TraderGroupPromiseLookupService,
} from "shared/cubiq-services";
import {
  TransferUtilitiesService,
  OrderXferTypeMap,
} from "app/bfx-blotter-orders/TransferUtilitiesService";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { LoggerService } from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { ColDef } from "ag-grid-community/dist/lib/entities/colDef";
import { GridOptions } from "ag-grid-community/dist/lib/entities/gridOptions";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxBlotterOrdersActionService } from "../bfx-blotter-orders/bfx-blotter-orders-action.service";
import { BfxBlotterOrdersService } from "../bfx-blotter-orders/bfx-blotter-orders.service";
import { BfxDialogCommand } from "../bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxDialogData } from "../bfx-dialogs/models/bfx-dialog-data.model";
import { BfxAutoCompleteComponent } from "../bfx-element-library/bfx-autocomplete.component";
import { ProgressStatus } from "../bfx-element-library/progress-bar-value.constants";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { GoldenLayoutPopout } from "../golden-layout/golden-layout-popout";
import { OrderTransferRecord } from "../../shared/cubiq-services/model/orders/OrderTransferRecord";
import { OrderTransferRecordState } from "../../shared/cubiq-services/model/orders/OrderTransferRecordState";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { NullProtoObject } from "../../shared/helper-services/null-proto-object";
import { TRANSFERED_OUT_OF_VISIBILITY } from "shared/cubiq-services/model/orders/TransferUtils";

const logger = LoggerService.for(__dirname);

const ERROR_ORDER_ALREADY_IN_TARGET: string = "order.take.already.in.group";
const TERMINAL_STATES = [CubiqTS.OrderState.FILLED, CubiqTS.OrderState.EXPIRED];
export const STATUS_FAILED: string = "Transfer Rejected";
export const MIN_VISIBLE_ROWS: number = 7;

type TransferType = "limit" | "algo" | "benchmark";

@Component({
  selector: "bfx-order-transfer",
  templateUrl: "./html/bfx-order-transfer.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxOrderTransferComponent implements GoldenLayoutComponent, OnDestroy, AfterViewInit {
  /**
   * An array of all selected orders for transfer
   */
  public orderRecords: Array<OrderTransferRecord> = [];

  public orderMap: NullProtoObject<OrderTransferRecord> = Object.create(null);

  public monitoredStrategyIds = new Set<string>();
  public pendingOrderRecords: Map<string, OrderTransferRecord[]> = new Map();
  public pendingDeletionSet: Set<string> = new Set();

  public showClose: boolean = true;
  public showTransfer: boolean = true;
  public showAbort: boolean = false;
  public showReject: boolean = false;
  public showAccept: boolean = false;
  public loading: boolean = true;

  public acceptLabel = signal("Accept");
  public rejectLabel = signal("Reject");

  public scrollWidth: string = "750px";
  public scrollHeight: string = "180px";
  public commentFocused: boolean = false;
  public comment: string = "";
  public message: string = "";
  public parentPopout: GoldenLayoutPopout = null;
  public progress: number = 0;
  public progressStatus: ProgressStatus = ProgressStatus.WAITING;
  public modelUpdateSubscription: Subscription;
  public modelDeleteSubscription: Subscription;
  public transferUpdateSubscription: Subscription;
  public freezeButtonUpdates: boolean = false;

  private canAcceptReject: boolean = false;

  private canAcceptExternal: boolean = false;
  private canRejectExternal: boolean = false;
  private canExternalAbort: boolean = false;
  private traderUnrestricted: boolean = false;

  public activeUser: ActiveUser = null;
  public externalTraderGroupIds: Set<string> = null;
  public invalidComment: boolean = false;

  public targetGroup:
    | {
        label: string;
        value: string;
      }
    | string = null;
  public targetGroups: Array<{
    label: string;
    value: string;
  }> = [];
  public limitTargetGroups: Array<{
    label: string;
    value: string;
  }> = [];
  public benchmarkTargetGroups: Array<{
    label: string;
    value: string;
  }> = [];
  public targetGroupSuggestions: Array<{
    label: string;
    value: string;
  }> = [];

  private scheduledGridRefresh: boolean = false;

  private transferType: TransferType = "limit";

  public get benchmarkTransfer(): boolean {
    return this.transferType === "benchmark";
  }

  public get algoTransfer(): boolean {
    return this.transferType === "algo";
  }

  public get limitTransfer(): boolean {
    return this.transferType === "limit";
  }

  public columnDefs: Array<ColDef | BfxColDef>;
  public gridOptions: GridOptions;

  public blotterInstance: any = null;

  protected _readyResFn;
  protected _readyRejFn;

  protected _ready: Promise<void> = new Promise<void>((res, rej) => {
    this._readyResFn = res;
    this._readyRejFn = rej;
  });

  public control: {
    mode: boolean;
    containerHeight: number;
    scrollbarConfig: {
      suppressScrollX: boolean;
    };
    traderGroupAutocompleteConfig: {
      allowEmpty: boolean;
      forceSelection: boolean;
      customValidation: any;
    };
  } = {
    mode: true,
    containerHeight: 324,
    scrollbarConfig: {
      suppressScrollX: true,
    },
    traderGroupAutocompleteConfig: {
      allowEmpty: false,
      forceSelection: true,
      customValidation: null,
    },
  };

  @ViewChild("targetInput", { static: true })
  traderGroupInput: BfxAutoCompleteComponent;

  constructor(
    protected columnSelectorService: BfxBaseBlotterColumnSelectorService,
    public orderActionService: OrderDataActionPromiseService,
    public orderAcceptService: OrderAcceptancePromiseService,
    public orderBlotterService: BfxBlotterOrdersService,
    public sysConfigService: SystemConfigPromiseService,
    public orderDataService: OrderDataPromiseLookupService,
    public tradeGroupService: TraderGroupPromiseLookupService,
    public activeUserService: ActiveUserProfilePromiseService,
    public blotterOrderActionService: BfxBlotterOrdersActionService,
    public systemConfigService: SystemConfigPromiseService,

    public orderXferService: OrderXferPromiseService,
    public element: ElementRef,
    public translationService: TranslateService,
    public benchmarkDataService: BenchmarkDataPrimaryLookupService,
    private notificationsService: NotificationService,
    private transferUtilitiesService: TransferUtilitiesService,
    private cd: ChangeDetectorRef,
  ) {
    this.blotterInstance = this;
    this.initialize();
  }

  protected initialize(): void {
    this.gridOptions = <GridOptions>{
      deltaRowDataMode: true,
      getRowNodeId: (data) => {
        if (data) return String(data["transferId"]);
        return "";
      },
      suppressContextMenu: true,
      headerHeight: 24,
      suppressColumnMoveAnimation: true,
      suppressDragLeaveHidesColumns: true,
      suppressScrollOnNewData: true,
      suppressFocusAfterRefresh: true,
      suppressAutoSize: true,
      showToolPanel: false,
      toolPanelSuppressSideButtons: true,
      enableFilter: true,
      enableBrowserTooltips: true,
      rowDeselection: true,
      onGridReady: () => {
        this.gridOptions.api.setRowData(this.orderRecords);
      },
    };
    // Column defs common(ish) with Cancel/Take/Force Take/Transfer/Pause
    this.columnDefs = [
      {
        headerName: "Order ID",
        field: "orderRef",
        editable: false,
        colId: "orderId",
        suppressMenu: true,
        omitFromPicker: false,
        hide: false,
        minWidth: 80,
        width: 125,
      },
      {
        headerName: "Order",
        field: "orderDescription",
        editable: false,
        colId: "order",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 250,
        width: 350,
      },
      {
        headerName: "Transfer From",
        field: "traderGroupName",
        editable: false,
        colId: "traderGroupName",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 90,
        width: 110,
      },
      {
        headerName: "Transfer Status",
        field: "orderStatus",
        editable: false,
        colId: "orderStatus",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 60,
        width: 100,
      },
      {
        headerName: "Reason",
        field: "reason",
        editable: false,
        colId: "reason",
        suppressMenu: true,
        omitFromPicker: false,
        disableInPicker: true,
        hide: false,
        minWidth: 250,
        width: 350,
      },
    ];
    NativeDesktopSupport.prepareGridOptions(this.gridOptions, this.element.nativeElement);

    Promise.all([
      this.systemConfigService.hasRole(Roles.DI_TRADER_ACCEPT),
      this.systemConfigService.hasRole(Roles.DI_TRADER_REJECT),
      this.systemConfigService.hasRole(Roles.DI_TRADER_UNRESTRICTED),
      this.systemConfigService.hasRole(Roles.DI_TRADER_TRANSFER),
      this.systemConfigService.hasRole(Roles.ORDER_EXTERNAL_TRANSFER_ABORT),
    ]).then(([accept, reject, unrestricted, traderTransfer, externalAbort]) => {
      this.canAcceptReject = traderTransfer;
      this.traderUnrestricted = unrestricted;
      this.canAcceptExternal = traderTransfer && (accept || unrestricted);
      this.canRejectExternal = traderTransfer && (reject || unrestricted);
      this.canExternalAbort = externalAbort;
    });

    this.activeUserService.getActiveUserData().then((data: ActiveUser) => {
      this.activeUser = data;
    });

    this.tradeGroupService.isPrimed().then(() => {
      this.tradeGroupService.getAll().then((values: Array<TradingGroupImpl>) => {
        this.limitTargetGroups = values
          .filter(
            (group: TradingGroupImpl) =>
              group.groupId.toNumber() >= 0 && !group.isBenchmarkFg && !group.isStagingFg,
          )
          .map((group: TradingGroupImpl) => {
            return {
              label: group.groupCode,
              value: group.groupId.toString(),
            };
          })
          .sort((a: any, b: any) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

        this.benchmarkTargetGroups = values
          .filter(
            (group: TradingGroupImpl) =>
              group.groupId.toNumber() >= 0 &&
              group.isBenchmarkFg &&
              !group.isAutoFilledFg &&
              !group.isExternallyFilledFg,
          )
          .map((group: TradingGroupImpl) => {
            return {
              label: group.groupCode,
              value: group.groupId.toString(),
            };
          })
          .sort((a: any, b: any) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

        this.externalTraderGroupIds = new Set(
          values
            .filter((traderGroup: TradingGroupImpl) => traderGroup.isExternallyFilledFg)
            .map((traderGroup: TradingGroupImpl) => traderGroup.groupId.toString()),
        );

        this.orderBlotterService.isReady().then(this._readyResFn);
      });
    });
  }

  ngAfterViewInit() {}

  rowStyle(rowData: any, index: number): string {
    return index % 2 == 0 ? "row-even" : "row-odd";
  }

  ngOnDestroy() {
    if (this.modelUpdateSubscription != null) {
      this.modelUpdateSubscription.unsubscribe();
    }

    if (this.modelDeleteSubscription != null) {
      this.modelDeleteSubscription.unsubscribe();
    }

    if (this.transferUpdateSubscription != null) {
      this.transferUpdateSubscription.unsubscribe();
    }
    this.orderRecords = null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  glInit(data: any, _container: any, _state: any): void {
    Promise.all([this._ready, this.sysConfigService.isPrimed()]).then(() => {
      if (data.benchmarks === true) {
        this.transferType = "benchmark";
        this.targetGroups = this.benchmarkTargetGroups;
        this.orderRecords = this.transferUtilitiesService.createBenchmarkTransferRecords(
          data,
          false,
        );
      } else if (data.algos === true) {
        this.transferType = "algo";
        this.targetGroups = this.limitTargetGroups;
        this.orderRecords = this.transferUtilitiesService.createOrderRecords(data, false, false);
      } else {
        this.transferType = "limit";
        this.targetGroups = this.limitTargetGroups;
        this.orderRecords = this.transferUtilitiesService.createLimitTransferRecords(data, false);
      }

      this.orderRecords.forEach((record: OrderTransferRecord) => {
        this.orderMap[record.transferId] = record;
        if (record.strategyId != null) {
          this.monitoredStrategyIds.add(record.strategyId);
        }
      });

      if (this.benchmarkTransfer) {
        this.modelDeleteSubscription = this.benchmarkDataService
          .getModelDeleteWithReasonSubject()
          .subscribe(this.onModelDeleted.bind(this));

        this.modelUpdateSubscription = this.benchmarkDataService
          .getModelUpdateSubject()
          .subscribe(this.onModelUpdated.bind(this));
      } else {
        this.modelDeleteSubscription = this.orderDataService
          .getModelDeleteWithReasonSubject()
          .subscribe(this.onModelDeleted.bind(this));

        this.modelUpdateSubscription = this.orderDataService
          .getModelUpdateSubject()
          .subscribe(this.onModelUpdated.bind(this));

        this.transferUpdateSubscription = this.orderDataService
          .getTransferUpdateSubject()
          .pipe(filter((value: any) => value.source == OrderDataModelUpdateSource.XferEvent))
          .subscribe(this.onModelUpdated.bind(this));
      }

      this.tradeGroupService.getAll().then((value: TradingGroupImpl[]) => {
        this.externalTraderGroupIds = new Set(
          value
            .filter((traderGroup: TradingGroupImpl) => traderGroup.isExternallyFilledFg)
            .map((traderGroup: TradingGroupImpl) => traderGroup.groupId.toString()),
        );
        this.orderRecords.forEach((orderRecord: OrderTransferRecord) => {
          let deskId: string =
            orderRecord.rawData != null && orderRecord.rawData.deskId != null
              ? orderRecord.rawData.deskId.toString()
              : "";
          orderRecord.external = this.externalTraderGroupIds.has(deskId);
        });
      });
    });

    setTimeout(() => {
      this.traderGroupInput.onInputFocus(event);
    }, 200);
  }

  public commentFocus(event: any): void {
    this.commentFocused = true;
    event.target.select();
  }

  protected onModelDeleted(event: ModelDeleteEvent): void {
    try {
      if (this.progressStatus != ProgressStatus.WAITING) {
        if (event.reason != null && event.reason === TRANSFERED_OUT_OF_VISIBILITY) {
          this.onModelUpdated([event]);
        } else {
          this.doDeletion(event.id);
          this.determineActions();
          if (Object.keys(this.orderMap).length == 0) {
            this.progressStatus = ProgressStatus.FAILURE;
            this.hideAllActionButtons();
            this.showClose = true;
          }
        }
      }
    } catch (err) {
      logger.out.error("Error processing model delete event ", err);
    }
    this.cd.markForCheck();
  }

  private doDeletion(transferId: string): void {
    let record: OrderTransferRecord = this.orderMap[transferId];
    if (record != null && record.state != OrderTransferRecordState.ABORT_REJECT) {
      let total: number = this.orderRecords.length;
      if (record.orderState == CubiqTS.OrderState.EXPIRED) {
        record.orderStatus = "Expired";
        record.reason = this.translationService.instant("order.notTransferable", [
          record.orderRef,
          record.orderStatus,
        ]);
      } else if (record.orderState == CubiqTS.OrderState.FILLED) {
        record.orderStatus = "Filled";
        record.reason = this.translationService.instant("order.notTransferable", [
          record.orderRef,
          record.orderStatus,
        ]);
      } else {
        record.orderStatus = "Transfer Aborted";
        record.reason = "Aborted by User";
      }
      record.state = OrderTransferRecordState.ABORT_REJECT;
      this.scheduleRefresh();
      delete this.orderMap[transferId];
      this.progress += 100 / total;
    }
  }

  /**
   * Get all related orders to those in the argument  array of orders
   * @param rows
   * @returns Row containing those orders that are in the same strategy as the arguments
   */
  protected findRelatedOrders(rows: Array<any>): Array<any> {
    return this.orderBlotterService.findRelatedOrders(rows);
  }

  protected onModelUpdated(event: any): void {
    if (event == null) {
      return;
    }
    if (Array.isArray(event)) {
      event.forEach(this.onModelUpdated.bind(this));
      return;
    } else if (
      Array.isArray(event.allocationData) &&
      event.allocationData?.[0].allocationType === CubiqTS.AllocationType.CROSS &&
      Array.isArray(event.allocationData?.[0].componentAllocations)
    ) {
      this.transferUtilitiesService
        .processCompositeAllocations([event])
        .forEach(this.onModelUpdated.bind(this));
      return;
    }

    this.updateOrderState(event);

    if (this.progressStatus != ProgressStatus.WAITING) {
      let transferId: string = this.transferUtilitiesService.getTransferId(
        event,
        this.benchmarkTransfer,
      );
      let record: OrderTransferRecord = this.orderMap[transferId];
      if (
        record != null &&
        (!this.benchmarkTransfer || record.state != OrderTransferRecordState.ABORT_REJECT)
      ) {
        if (event.orderId != null) {
          record.rawData = event;
        }

        let type: XferType = event.xferEventType;
        record.state = OrderXferTypeMap[XferType[type]];
        record.orderStatus = this.translationService.instant(
          "order.transfer.type." + XferType[type].toLowerCase(),
        );
        if (
          (event.xferEventType == XferType.XFER_ACCEPT ||
            event.xferEventType == XferType.XFER_REJECT ||
            event.xferEventType == XferType.XFER_CANCEL ||
            event.xferEventType == XferType.XFER_STEAL) &&
          event.xferUserName != null
        ) {
          if (event.xferTargetGroup != null) {
            record.reason = this.translationService.instant(
              "order.transfer.reson." + XferType[event.xferEventType].toLowerCase(),
              [event.xferUserName, event.xferTargetGroup],
            );
          } else {
            record.reason = this.translationService.instant(
              "order.transfer.reson." + XferType[event.xferEventType].toLowerCase(),
              [event.xferUserName],
            );
          }
        } else {
          record.reason = event.xferComment;
        }
      }

      this.scheduleRefresh();
    }
  }

  updateOrderState(event: any) {
    const transferId = this.transferUtilitiesService.getTransferId(event, this.benchmarkTransfer);
    let orderRecords: OrderTransferRecord[];
    let orderRecord: OrderTransferRecord = this.orderMap[transferId] ?? null;

    if (orderRecord != null) {
      orderRecord.orderState = event.orderState;
    } else if (orderRecord == null && this.monitoredStrategyIds.has(event.strategyId?.toString())) {
      if (this.benchmarkTransfer) {
        orderRecords = this.transferUtilitiesService.createBenchmarkTransferRecords([event], false);
      } else {
        orderRecords = this.transferUtilitiesService.createOrderRecords([event], false, false);
      }
      this.pendingOrderRecords.set(transferId, orderRecords);

      if (TERMINAL_STATES.includes(orderRecord.orderState)) {
        this.pendingDeletionSet.add(transferId);
      } else {
        this.pendingDeletionSet.delete(transferId);
      }
    }
  }

  protected scheduleRefresh(delay: number = 500): void {
    if (!this.scheduledGridRefresh) {
      this.scheduledGridRefresh = true;
      setTimeout(() => {
        if (this.gridOptions && this.gridOptions.api) {
          this.gridOptions.api.refreshCells({
            columns: ["orderStatus", "reason"],
          });
        }
        this.determineActions();
        this.scheduledGridRefresh = false;
      }, delay);
    }
  }

  protected determineActions(): void {
    if (!this.freezeButtonUpdates && this.progressStatus == ProgressStatus.RUNNING) {
      this.hideAllActionButtons();

      let acceptanceRecords: Array<OrderTransferRecord> = [];
      let rejectRecordsCount: number = 0;
      let cancelledRecordCount: number = 0;
      let inProgressCount: number = 0;

      this.orderRecords.forEach((record: OrderTransferRecord) => {
        if (record.state == OrderTransferRecordState.ACCEPTANCE) {
          acceptanceRecords.push(record);
        } else if (record.state == OrderTransferRecordState.ABORT_REJECT) {
          rejectRecordsCount++;
        } else if (record.state == OrderTransferRecordState.TRANSFERED) {
          cancelledRecordCount++;
        } else if (record.state == OrderTransferRecordState.IN_PROGRESS) {
          inProgressCount++;
        }
      });
      if (inProgressCount == 0 && typeof this.targetGroup !== "string") {
        let external: boolean = this.externalTraderGroupIds.has(this.targetGroup.value);
        let hasAcceptance: boolean = acceptanceRecords.length > 0;
        if (hasAcceptance) {
          if (external) {
            this.showAccept = this.canAcceptExternal;
            this.showReject = this.canRejectExternal;
            this.acceptLabel.set("Force Accept");
            this.rejectLabel.set("Force Reject");
          } else {
            let ownsEvery: boolean = acceptanceRecords.every((record: OrderTransferRecord) => {
              let traderGroupId: string = (
                record.rawData as CubiqTS.OrderData
              )?.targetDeskId?.toString();
              return (
                record.state != OrderTransferRecordState.ACCEPTANCE ||
                this.activeUser.tradingGroupsIds.includes(traderGroupId)
              );
            });

            this.showAccept = this.canAcceptReject && (ownsEvery || this.traderUnrestricted);
            this.showReject = this.canAcceptReject && (ownsEvery || this.traderUnrestricted);
            this.acceptLabel.set("Accept");
            this.rejectLabel.set("Reject");
          }
          this.showAbort = !external || this.canExternalAbort;
        }
      }

      let finishedCount: number = cancelledRecordCount + rejectRecordsCount;
      this.progress =
        (this.orderRecords.length - inProgressCount) * (100 / this.orderRecords.length);

      let allFinished: boolean = finishedCount == this.orderRecords.length;
      if (allFinished) {
        if (cancelledRecordCount == this.orderRecords.length) {
          this.success();
        } else {
          if (rejectRecordsCount > 0) {
            this.fail(
              rejectRecordsCount,
              rejectRecordsCount == 1 ? "transfer.reject.single" : "transfer.reject.multi",
            );
          }
        }
      }
    }
    this.cd.markForCheck();
  }

  /**
   * Searches the target group suggestions for a infix matching of the given filter string
   * @param filter the filter should be checked to be an infix of the target trader group
   */
  public searchTargetGroup(filter: any): void {
    let query: string = filter.query.toLowerCase();
    if (query.length > 0) {
      this.targetGroupSuggestions = this.targetGroups.filter((tradeGroup: any) =>
        tradeGroup.label.toLowerCase().includes(query),
      );
    } else {
      this.targetGroupSuggestions = this.targetGroups.slice(0);
    }
  }

  public onSelectTargetGroup(data: any): void {
    this.validateTargetGroup();
  }

  /**
   * Main abort entry point which determines if a user needs to confirm the abort
   * action for external orders
   * @returns
   */
  public abort(): void {
    if (typeof this.targetGroup === "string") return;

    this.hideAllActionButtons();

    let ordersExternal: boolean = this.externalTraderGroupIds.has(this.targetGroup.value);
    let externalCount: number = this.orderRecords.length;

    if (ordersExternal) {
      let msg: string =
        externalCount == 1 ? "transfer.order.external.single" : "transfer.order.external.multi";
      let payload: any = {
        messages: [this.translationService.instant(msg)],
        title: "transfer.order.external.title",
        callback: this.onAbort.bind(this),
      };
      this.openConfirmDialog(payload);
      this.determineActions();
    } else {
      this.onAbort();
    }
  }

  /**
   * Abort method called after any required confirmations have been accepted by the user
   */
  private onAbort() {
    this.hideAllActionButtons();

    let ids: Array<string> = this.orderRecords
      .filter(
        (record: OrderTransferRecord) =>
          record.state == OrderTransferRecordState.ACCEPTANCE && !record.external,
      )
      .map((record: OrderTransferRecord) => {
        record.state = OrderTransferRecordState.IN_PROGRESS;
        return record.transferId;
      });

    this.orderXferService
      .requestXfer(ids, null, XferAction.CANCEL, "0", this.benchmarkTransfer)
      .then((response: any) => {
        this.handleAcceptanceActionFail(response, BFXNotificationSubType.ABORT_FAIL, ids);
      });
  }

  public accept(): void {
    if (typeof this.targetGroup === "string") return;

    this.hideAllActionButtons();

    let ordersExternal: boolean = this.externalTraderGroupIds.has(this.targetGroup.value);
    let externalCount: number = this.orderRecords.length;

    if (ordersExternal) {
      let msg: string =
        externalCount == 1 ? "transfer.order.external.single" : "transfer.order.external.multi";
      let payload: any = {
        messages: [this.translationService.instant(msg)],
        title: "transfer.order.external.title",
        callback: this.onAccept.bind(this),
      };
      this.openConfirmDialog(payload);
      this.determineActions();
    } else {
      this.onAccept();
    }
  }

  protected onAccept(): void {
    if (typeof this.targetGroup === "string") return;

    let ids: Array<string> = this.orderRecords
      .filter((record: OrderTransferRecord) => record.state == OrderTransferRecordState.ACCEPTANCE)
      .map((value: OrderTransferRecord) => {
        value.state = OrderTransferRecordState.IN_PROGRESS;
        return value.transferId;
      });

    this.orderXferService
      .requestXfer(
        ids,
        this.comment,
        XferAction.ACCEPT,
        this.targetGroup.value,
        this.benchmarkTransfer,
      )
      .then((response: any) => {
        this.handleAcceptanceActionFail(response, BFXNotificationSubType.ACCEPTANCE_FAIL, ids);
      });
  }

  public reject(): void {
    if (typeof this.targetGroup === "string") return;

    this.hideAllActionButtons();

    let ordersExternal: boolean = this.externalTraderGroupIds.has(this.targetGroup.value);
    let externalCount: number = this.orderRecords.length;

    if (ordersExternal) {
      let msg: string =
        externalCount == 1 ? "transfer.order.external.single" : "transfer.order.external.multi";
      let payload: any = {
        messages: [this.translationService.instant(msg)],
        title: "transfer.order.external.title",
        callback: this.onReject.bind(this),
      };
      this.openConfirmDialog(payload);
      this.determineActions();
    } else {
      this.onReject();
    }
  }

  public onReject(): void {
    if (typeof this.targetGroup === "string") return;

    let ids: Array<string> = this.orderRecords
      .filter((record: OrderTransferRecord) => record.state == OrderTransferRecordState.ACCEPTANCE)
      .map((value: OrderTransferRecord) => {
        value.state = OrderTransferRecordState.IN_PROGRESS;
        return value.transferId;
      });

    this.orderXferService
      .requestXfer(
        ids,
        this.comment,
        XferAction.REJECT,
        this.targetGroup.value,
        this.benchmarkTransfer,
      )
      .then((response: any) => {
        this.handleAcceptanceActionFail(response, BFXNotificationSubType.REJECTIONS_FAIL, ids);
      });
  }

  resizeDocked(event: any) {}

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopout = parentPopup;
    this.parentPopout.setShowDock(false);
  }

  /**
   * Invoked by the close confirmation warning dialog if the
   * user chooses to go ahead and close the window
   */
  public confirmClose(): void {
    this.progressStatus = ProgressStatus.FAILURE;
    this.parentPopout.close();
  }

  /**
   * Callback that determines if this popup should be closed or not
   */
  public onClose(): boolean {
    if (this.progressStatus == ProgressStatus.RUNNING && !this.parentPopout.closeFromLogout) {
      let orderCount: number = this.orderRecords.length;
      let msg: string =
        orderCount == 1 ? "transfer.order.confirm.single" : "transfer.order.confirm.multi";
      let payload: any = {
        messages: [this.translationService.instant(msg)],
        title: "confirm.close.title",
        callback: this.confirmClose.bind(this),
        subMessage: this.translationService.instant("order_entry.dialog.close.subMessage"),
      };
      this.openConfirmDialog(payload);
      return false;
    }

    return true;
  }

  public showColumnSelector(event): void {
    this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
  }

  // handle the click of the ag-grid column header and header
  public handleClick(event) {
    if (event.which == 3 && event.target.className.indexOf("ag-header-cell") !== -1) {
      this.showColumnSelector(event);
    } else if (event.which == 3 && event.target.className.indexOf("ag-header-container") !== -1) {
      this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
    }
  }

  /**
   *
   */
  protected openConfirmDialog(payload: any): void {
    let bfxDialogData: BfxDialogData = new BfxDialogData(
      BfxDialogCommand.DIALOG_ORDER_CANCEL_CONFIRM_OPEN,
      null,
      null,
      payload,
    );

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(this.element.nativeElement);
      bfxDialogData.body = wnd.getNativeWindow().document.body;
    }

    this.blotterOrderActionService.launchNewDialog(bfxDialogData);
  }

  /**
   * Invoked when the 'Transfer Order' button is pressed. In case the
   * comment is mandatory the comment input will be focussed, otherwise
   * the transfer execution flow is run.
   * @param event the event that triggered invocation of this method
   * @param targetInput the target group input reference
   */
  public transferOrders(event: any, targetInput: any): void {
    if (!this.targetGroupValid || typeof this.targetGroup === "string") {
      let input = this.getAutoSuggestInput(targetInput);
      input.focus();
      return;
    }
    let refresh = false;
    this.pendingOrderRecords.forEach((orderRecords: OrderTransferRecord[], transferId, map) => {
      map.delete(transferId);
      orderRecords.forEach((record) => {
        this.orderMap[record.transferId] = record;
        this.orderRecords.push(record);
        refresh = true;
      });
    });
    this.pendingDeletionSet.forEach((transferId, _, set) => {
      set.delete(transferId);
      this.doDeletion(transferId);
      refresh = true;
    });
    if (refresh) {
      this.gridOptions?.api?.setRowData(this.orderRecords);
    }
    let transferIds: Array<string> = this.orderRecords.map(
      (order: OrderTransferRecord) => order.transferId,
    );

    this.hideAllActionButtons();
    this.progressStatus = ProgressStatus.RUNNING;

    let errorCount: number = 0;
    this.orderRecords.forEach((order: OrderTransferRecord) => {
      let deskId: string = (order.rawData as CubiqTS.OrderData)?.deskId?.toString();
      if (this.benchmarkTransfer) {
        let allocationData: CubiqTS.AllocationDataImpl = order.rawData.allocationData[0];
        deskId = allocationData.deskId?.toString();
      }

      if (typeof this.targetGroup != "string" && this.targetGroup.value == deskId) {
        order.state = OrderTransferRecordState.ABORT_REJECT;
        order.orderStatus = STATUS_FAILED;
        order.reason = this.translationService.instant(ERROR_ORDER_ALREADY_IN_TARGET, [
          deskId,
          this.targetGroup.label,
        ]);
        transferIds.splice(transferIds.indexOf(order.transferId), 1);
        errorCount++;
      }
    });

    if (errorCount == this.orderRecords.length) {
      this.fail(errorCount);
      return;
    }

    this.orderXferService
      .requestXfer(
        transferIds,
        this.comment,
        XferAction.REQUEST,
        this.targetGroup.value,
        this.benchmarkTransfer,
      )
      .then((value: any) => {
        if (value.encodableType == ErrorsImpl.enType) {
          this.orderRecords.forEach((record: OrderTransferRecord) => {
            record.state = OrderTransferRecordState.ABORT_REJECT;
            record.orderStatus = STATUS_FAILED;
            this.updateOrderReason(record, value["errorMessages"][0]);
          });

          this.fail(this.orderRecords.length);
        } else if (value.results && value.results.length > 0) {
          let erroredOrderIdSet = new Set<string>();

          value.results.forEach((result: any) => {
            let transferResult: OrderTransferResultImpl = result;
            let record: OrderTransferRecord = this.orderMap[transferResult.orderId];

            if (record != null) {
              if (!transferResult.success) {
                record.state = OrderTransferRecordState.ABORT_REJECT;
                record.orderStatus = STATUS_FAILED;
                this.updateOrderReason(record, transferResult.errors["errorMessages"][0]);
                erroredOrderIdSet.add(record.transferId);
              } else {
                record.state = OrderTransferRecordState.TRANSFERED;
                record.orderStatus = "Transfer Success";
              }
            }
          });

          // GFT based rejections in T_TRDER_GROUP_RESTRICTIONS
          // Generate two rejection messages per order id
          if (erroredOrderIdSet.size == this.orderRecords.length) {
            this.fail(erroredOrderIdSet.size);
          }

          this.scheduleRefresh();
        } else {
          this.orderRecords
            .filter((record: OrderTransferRecord) => transferIds.indexOf(record.transferId) >= 0)
            .forEach((record: OrderTransferRecord) => {
              if (record.state == OrderTransferRecordState.IN_PROGRESS) {
                record.orderStatus = "Transfer Requested";
              }
            });

          this.scheduleRefresh();
        }
      })
      .catch((reason: any) => {
        this.fail(this.orderRecords.length);
      });
  }

  public getAutoSuggestInput(element: ElementRef): any {
    element.nativeElement.firstElementChild.firstElementChild;
  }

  public validateTargetGroup(): void {
    if (this.targetGroup != null && typeof this.targetGroup === "string") {
      let targetGroup = this.targetGroups.find(
        (group: any) => group.label.toLowerCase() == (this.targetGroup as string).toLowerCase(),
      );

      if (targetGroup != null) {
        this.targetGroup = targetGroup;
      }
    }
  }

  public get targetGroupValid(): boolean {
    return this.targetGroup != null && typeof this.targetGroup != "string";
  }

  public validateCommentInput(event): void {
    this.comment = this.comment.trim();
    this.invalidComment = false;
    this.commentFocused = false;
  }

  /**
   * Updates order cancel record reason field if error is properly supplied
   */
  protected updateOrderReason(orderRecord: OrderTransferRecord, error: any): void {
    if (error != null && error.length > 1) {
      orderRecord.reason = this.convertError(error);
    }
  }

  /**
   * Creates a translation object with a message as id and
   * parameters as args list from a string
   * @param error the error string as served in ErrorsImpl object
   */
  protected convertError(error: any): string {
    let errorObject = JSON.parse(error + "");

    if (errorObject.length) {
      errorObject = JSON.parse(errorObject[0]);
    }

    return this.translationService.instant(errorObject.id, errorObject.args);
  }

  /**
   * Convenience method that sets the final state of the window to success.
   * @param errorCount the number of messages that have failed/rejected
   * @param message the message or default error message if left out
   */
  protected success(): void {
    this.hideAllActionButtons();
    this.scheduleRefresh();
    this.progress = 100;
    this.showClose = true;
    this.progressStatus = ProgressStatus.SUCCESS;
    this.cd.markForCheck();
  }

  /**
   * Convenience method that sets the final state of the window to failure.
   * @param errorCount the number of messages that have failed/rejected
   * @param message the message or default error message if left out
   */
  protected fail(errorCount: number, message: string = "transfer.error"): void {
    this.message = this.translationService.instant(message, [errorCount]);
    this.hideAllActionButtons();
    this.scheduleRefresh();
    this.progress = 100;
    this.showClose = true;
    this.progressStatus = ProgressStatus.FAILURE;
    this.cd.markForCheck();
  }

  /**
   * Hides all action buttons placed at the bottom right of the dialog (abort, accept, reject, cancel and close)
   * @param freeze indicates if further button determination should occur
   */
  protected hideAllActionButtons(freeze: boolean = false): void {
    this.freezeButtonUpdates = freeze;
    this.showAbort = this.showAccept = this.showTransfer = this.showClose = this.showReject = false;
    this.cd.markForCheck();
  }

  private handleAcceptanceActionFail(
    response: any,
    notificationSubType: BFXNotificationSubType,
    localIds: Array<string>,
  ): void {
    if (response instanceof CubiqTS.ErrorsImpl) {
      let localOrderIds = new Array<string>();

      if (Array.isArray(this.orderRecords) && this.orderRecords.length > 0) {
        this.orderRecords
          .filter(
            (record: OrderTransferRecord) =>
              record.state == OrderTransferRecordState.IN_PROGRESS &&
              localIds.includes(record.transferId),
          )
          .forEach((record: OrderTransferRecord) => {
            record.state = OrderTransferRecordState.ACCEPTANCE;
            localOrderIds.push(record.transferId);
          });
      }

      if (
        ((response as any).orderIds == null ||
          (Array.isArray((response as any).orderIds) && (response as any).orderIds.length === 0)) &&
        Array.isArray(localOrderIds) &&
        localOrderIds.length > 0
      ) {
        (response as any).orderIds = localOrderIds;
      }

      this.notificationsService.handleErrors(
        response,
        BFXNotificationType.ORDER_ACTION_FAIL,
        notificationSubType,
      );
      this.determineActions();
    }
  }
}
